'use memo';

import { memo, useEffect, useRef } from 'react';
import Icon from '@/components/Icon/Icon';
import { cn } from '@/lib/cn';
type ScrollButtonProps = {
  /** スクロールの方向 */
  direction: 'left' | 'right';
  /** クリックハンドラー */
  onClick: () => void;
  /** 無効状態 */
  disabled?: boolean;
  /**
   * 背景色のバリアント
   * - fill: `Neutral/Fill`
   * - bottomFill: `Neutral/Bottom Fill`
   */
  variant?: 'fill' | 'bottomFill';
  /** カスタムクラス名 */
  className?: string;
};

/**
 * スクロールボタン（シェブロン）
 *
 * 水平スクロール可能なUIで左右スクロールを行うボタン。
 * disabled 時も DOM を維持し、フォーカスと支援技術の整合性を保つ。
 * 不可視になったボタンはタブ順と支援技術から外す。
 * disabled 遷移時の blur は、不可視ボタンへフォーカスが残らないようにするため。
 */
export const ScrollButton = memo(function ScrollButton({
  direction,
  onClick,
  disabled,
  variant = 'fill',
  className
}: ScrollButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  // 不可視になったボタンへフォーカスが残らないようにする
  useEffect(() => {
    if (disabled) {
      buttonRef.current?.blur();
    }
  }, [disabled]);
  const isLeft = direction === 'left';
  const iconName = isLeft ? 'ChevronLeft' : 'ChevronRight';
  const ariaLabel = isLeft ? 'Scroll left' : 'Scroll right';
  const backgroundClassName = variant === 'bottomFill' ? 'bg-neutral-bottom-fill' : 'bg-neutral-fill';
  return <div className={cn('absolute z-10 flex items-center pointer-events-none h-9 w-12', isLeft ? 'left-0 justify-start' : 'right-0 justify-end', disabled && 'opacity-0', className)}>
      {/* 影だけ上下をクリップする（focus ring はクリップしない） */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
        <div className={cn('absolute top-0 size-9 pointer-events-none', isLeft ? 'left-0 shadow-[4px_0px_4px_0px_rgba(0,0,0,0.1)]' : 'right-0 shadow-[-4px_0px_4px_0px_rgba(0,0,0,0.1)]')} />
      </div>
      <button ref={buttonRef} type="button" aria-label={ariaLabel} aria-hidden={disabled || undefined} tabIndex={disabled ? -1 : undefined} disabled={disabled} onClick={onClick} className={cn('pointer-events-auto relative flex items-center justify-center p-2 size-9 border-0 rounded-none', backgroundClassName, 'text-neutral-muted-on-fill', 'focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring', 'transition-colors hover:bg-neutral-fill-hovered', disabled && 'pointer-events-none')}>
        <span aria-hidden="true" className={cn('absolute top-0 h-full w-px bg-neutral-muted-border', isLeft ? 'right-0' : 'left-0')} />
        <Icon iconName={iconName} size={20} inheritColor />
      </button>
    </div>;
});